"use client";

import { isManufacturerSponsor } from "@/utils/isManufacturerSponsor";
import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import checkAddToCart, { AddToCartStatus } from "../checkAddToCart";
import BuyBoxTile, { BuyBoxTileCategory } from "./buyBoxTile";
import style from "./vendorTiles.module.scss";

export type VendorTilesProps = {
  vendorOptions: VendorOptionState[];
  chosenVendorKey: number;
  setChosenVendorKey: (index: number) => void;
  quantity: number;
  sponsoredVendorId?: number | string;
  isDefaultVendor?: boolean;
  productBuyBoxPreference: BuyBoxPreference[];
  isCalculating?: boolean;
  initialChosenVendorKey?: number;
  hasVidFromServer?: boolean;
  onSeeAllOptions?: () => void;
};

const getVendorForCategory = (
  category: BuyBoxTileCategory,
  vendorOptions: VendorOptionState[],
  sponsoredVendorId?: number | string,
  isDefaultVendor?: boolean,
  initialChosenVendorKey?: number,
): { vendor: VendorOptionState | null; index: number } => {
  if (category === "default") {
    if (
      initialChosenVendorKey !== undefined &&
      initialChosenVendorKey >= 0 &&
      vendorOptions.length > initialChosenVendorKey
    ) {
      // vendor sponsored
      if (sponsoredVendorId) {
        const sponsoredVendorOffering = isDefaultVendor
          ? vendorOptions.findIndex((ver) => ver.vendorId == sponsoredVendorId)
          : 0;

        if (sponsoredVendorOffering >= 0) {
          return {
            vendor: vendorOptions[sponsoredVendorOffering],
            index: sponsoredVendorOffering,
          };
        }
      }

      return {
        vendor: vendorOptions[initialChosenVendorKey],
        index: initialChosenVendorKey,
      };
    }
    if (vendorOptions.length > 0) {
      return { vendor: vendorOptions[0], index: 0 };
    }
    return { vendor: null, index: -1 };
  }

  if (category === "authorized") {
    // First, try to find vendor with authorizedWinner === true
    const authorizedIndex = vendorOptions.findIndex(
      (vo) => vo.authorizedWinner === true && vo.badgeId && vo.badgeId > 0,
    );

    if (authorizedIndex >= 0) {
      return { vendor: vendorOptions[authorizedIndex], index: authorizedIndex };
    }

    // If no authorizedWinner, find vendor with badgeId > 0 and max inventory
    const authorizedVendors = vendorOptions
      .map((vo, index) => ({ vo, index }))
      .filter(({ vo }) => vo.badgeId && vo.badgeId > 0);

    if (authorizedVendors.length > 0) {
      const maxInventoryVendor = authorizedVendors.reduce((max, current) =>
        current.vo.inventory > max.vo.inventory ? current : max,
      );
      return { vendor: maxInventoryVendor.vo, index: maxInventoryVendor.index };
    }

    return { vendor: null, index: -1 };
  }

  if (category === "lowest-price") {
    const lowestIndex = vendorOptions.findIndex((vo) => vo.lowestPriceWinner === true);

    if (lowestIndex >= 0) {
      return { vendor: vendorOptions[lowestIndex], index: lowestIndex };
    }
  }

  if (category === "fastest-shipping") {
    const fastestIndex = vendorOptions.findIndex((vo) => vo.fastDeliveryWinner === true);

    if (fastestIndex >= 0) {
      return { vendor: vendorOptions[fastestIndex], index: fastestIndex };
    }
  }

  return { vendor: null, index: -1 };
};

const canVendorSupportQuantity = (
  vendorOption: VendorOptionState,
  quantity: number,
  sponsoredVendorId?: number | string,
  category?: string,
  vendorOptions?: VendorOptionState[],
): { canSupport: boolean; maxQty?: number } => {
  const status = checkAddToCart(quantity, vendorOption.minimumQuantityPurchasable, vendorOption.inventory);

  // Skip minimum quantity check only for sponsored vendor in default category
  if (sponsoredVendorId && category === "default") {
    // Resolve actual vendor ID for manufacturer sponsors (don't mutate the parameter)
    let resolvedSponsoredVendorId: number | string | undefined = sponsoredVendorId;

    // Get actual ID map to Manufacturer sponsor from vendorOptions list
    if (isManufacturerSponsor(resolvedSponsoredVendorId.toString()) && vendorOptions && vendorOptions.length > 0) {
      resolvedSponsoredVendorId = vendorOptions[0]?.vendorId;
    }

    // Only bypass minimum quantity check if THIS vendor matches the sponsored vendor ID
    const isThisVendorSponsored =
      resolvedSponsoredVendorId !== undefined && vendorOption.vendorId === resolvedSponsoredVendorId;

    if (isThisVendorSponsored && vendorOption.inventory >= quantity) {
      return { canSupport: true };
    }
  }

  if (status === AddToCartStatus.OK) {
    return { canSupport: true };
  }
  return { canSupport: false, maxQty: vendorOption.inventory };
};

const VendorTiles: FunctionComponent<VendorTilesProps> = ({
  vendorOptions,
  chosenVendorKey,
  setChosenVendorKey,
  quantity,
  sponsoredVendorId,
  isDefaultVendor = false,
  productBuyBoxPreference,
  isCalculating = false,
  initialChosenVendorKey,
  hasVidFromServer = false,
  onSeeAllOptions,
}) => {
  const [hasVidInUrlState, setHasVidInUrlState] = useState<boolean>(hasVidFromServer);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkVidInUrl = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasVid = urlParams.has("vid");
      setHasVidInUrlState((prev) => {
        if (prev !== hasVid) {
          return hasVid;
        }
        return prev;
      });
    };

    checkVidInUrl();
    window.addEventListener("popstate", checkVidInUrl);
    const intervalId = setInterval(checkVidInUrl, 300);

    return () => {
      window.removeEventListener("popstate", checkVidInUrl);
      clearInterval(intervalId);
    };
  }, []);

  const shouldShowDefaultVendor =
    hasVidInUrlState && initialChosenVendorKey !== undefined && initialChosenVendorKey >= 0;

  const defaultVendor = useMemo(
    () => getVendorForCategory("default", vendorOptions, sponsoredVendorId, isDefaultVendor, initialChosenVendorKey),
    [vendorOptions, sponsoredVendorId, isDefaultVendor, initialChosenVendorKey, quantity],
  );

  const authorizedVendor = useMemo(
    () => getVendorForCategory("authorized", vendorOptions, sponsoredVendorId, isDefaultVendor, initialChosenVendorKey),
    [vendorOptions, sponsoredVendorId, isDefaultVendor, initialChosenVendorKey, quantity],
  );

  const lowestPriceVendor = useMemo(
    () =>
      getVendorForCategory("lowest-price", vendorOptions, sponsoredVendorId, isDefaultVendor, initialChosenVendorKey),
    [vendorOptions, sponsoredVendorId, isDefaultVendor, initialChosenVendorKey, quantity],
  );

  const fastestShippingVendor = useMemo(
    () =>
      getVendorForCategory(
        "fastest-shipping",
        vendorOptions,
        sponsoredVendorId,
        isDefaultVendor,
        initialChosenVendorKey,
      ),
    [vendorOptions, sponsoredVendorId, isDefaultVendor, initialChosenVendorKey, quantity],
  );

  const tiles = useMemo(() => {
    const nameToCategoryKey: Record<string, BuyBoxTileCategory> = {
      "Default Vendor": "default",
      "Authorized Distributor": "authorized",
      "Lowest Price": "lowest-price",
      "Fast Delivery": "fastest-shipping",
    };

    const entries: Array<{ category: BuyBoxTileCategory; vendor: VendorOptionState; index: number }> = [];

    const vendorLookup: Record<BuyBoxTileCategory, { vendor: VendorOptionState | null; index: number }> = {
      default: defaultVendor,
      authorized: authorizedVendor,
      "lowest-price": lowestPriceVendor,
      "fastest-shipping": fastestShippingVendor,
    };

    if (shouldShowDefaultVendor && defaultVendor.vendor && defaultVendor.index >= 0) {
      const hasDefaultInPreferences = productBuyBoxPreference.some((pref) => pref.name === "Default Vendor");
      if (!hasDefaultInPreferences) {
        entries.push({
          category: "default",
          vendor: defaultVendor.vendor,
          index: defaultVendor.index,
        });
      }
    }

    if (sponsoredVendorId && defaultVendor.vendor && defaultVendor.index >= 0) {
      const alreadyHasDefault = entries.some((e) => e.category === "default");
      if (!alreadyHasDefault) {
        entries.push({
          category: "default",
          vendor: defaultVendor.vendor,
          index: defaultVendor.index,
        });
      }
    }

    productBuyBoxPreference
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .forEach(({ name }) => {
        const cat = nameToCategoryKey[name];
        const entry = vendorLookup[cat];

        if (cat === "default" && !shouldShowDefaultVendor) {
          return;
        }

        if (cat === "default" && shouldShowDefaultVendor && defaultVendor.vendor && defaultVendor.index >= 0) {
          const alreadyAdded = entries.some((e) => e.category === "default");
          if (alreadyAdded) {
            return;
          }
        }

        if (cat === "authorized" && (!entry?.vendor || entry.index < 0)) {
          return;
        }
        if (entry?.vendor && entry.index >= 0) {
          entries.push({
            category: cat,
            vendor: entry.vendor,
            index: entry.index,
          });
        }
      });

    return entries;
  }, [
    isDefaultVendor,
    defaultVendor,
    authorizedVendor,
    fastestShippingVendor,
    lowestPriceVendor,
    productBuyBoxPreference,
    shouldShowDefaultVendor,
    sponsoredVendorId,
  ]);

  const initialSelectedCategory = useMemo(() => {
    if (shouldShowDefaultVendor && defaultVendor.vendor && defaultVendor.index >= 0) {
      return "default";
    }

    const firstSupportedTile = tiles.find(
      (t) => canVendorSupportQuantity(t.vendor, quantity, sponsoredVendorId, t.category, vendorOptions).canSupport,
    );

    return firstSupportedTile ? firstSupportedTile.category : null;
  }, [
    shouldShowDefaultVendor,
    defaultVendor.vendor,
    defaultVendor.index,
    tiles,
    quantity,
    sponsoredVendorId,
    vendorOptions,
  ]);

  const initialSelectedTile = useMemo(() => {
    if (initialSelectedCategory) {
      return tiles.find((t) => t.category === initialSelectedCategory);
    }
    return null;
  }, [initialSelectedCategory, tiles]);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialSelectedCategory);
  const [hasInitializedDefault, setHasInitializedDefault] = useState(false);
  const hasInitializedDefaultRef = useRef(false);
  const userHasSelectedRef = useRef(false);

  useEffect(() => {
    if (userHasSelectedRef.current) {
      return;
    }

    if (initialSelectedTile && !hasInitializedDefaultRef.current) {
      if (shouldShowDefaultVendor && defaultVendor.vendor && defaultVendor.index >= 0) {
        setChosenVendorKey(defaultVendor.index);
      } else {
        setChosenVendorKey(initialSelectedTile.index);
      }
      hasInitializedDefaultRef.current = true;
      setHasInitializedDefault(true);
    }

    if (!hasVidInUrlState && hasVidFromServer && hasInitializedDefault) {
      setHasInitializedDefault(false);
      hasInitializedDefaultRef.current = false;
      userHasSelectedRef.current = false;
      setSelectedCategory(initialSelectedCategory);
      if (initialSelectedTile) {
        setChosenVendorKey(initialSelectedTile.index);
      }
    }
  }, [
    hasVidInUrlState,
    shouldShowDefaultVendor,
    defaultVendor.vendor,
    defaultVendor.index,
    setChosenVendorKey,
    hasInitializedDefault,
    initialSelectedCategory,
    initialSelectedTile,
  ]);

  useEffect(() => {
    if (userHasSelectedRef.current) {
      return;
    }

    if (!hasInitializedDefaultRef.current && initialSelectedCategory && selectedCategory !== initialSelectedCategory) {
      const selectedTile = tiles.find((t) => t.category === initialSelectedCategory);
      if (selectedTile) {
        setSelectedCategory(initialSelectedCategory);
        setChosenVendorKey(selectedTile.index);
        hasInitializedDefaultRef.current = true;
      }
    }
  }, [
    shouldShowDefaultVendor,
    defaultVendor.vendor,
    defaultVendor.index,
    setChosenVendorKey,
    tiles,
    initialSelectedCategory,
    selectedCategory,
  ]);

  useEffect(() => {
    if (selectedCategory) {
      const currentCategoryTile = tiles.find((t) => t.category === selectedCategory);

      if (currentCategoryTile) {
        const isDefaultCategory = currentCategoryTile.category === "default";
        const supportsQuantity =
          isDefaultCategory && !sponsoredVendorId
            ? true
            : canVendorSupportQuantity(
                currentCategoryTile.vendor,
                quantity,
                sponsoredVendorId,
                currentCategoryTile.category,
                vendorOptions,
              ).canSupport;

        if (userHasSelectedRef.current && supportsQuantity) {
          if (currentCategoryTile.index !== chosenVendorKey) {
            setChosenVendorKey(currentCategoryTile.index);
          }
          return;
        }

        if (supportsQuantity && currentCategoryTile.index === chosenVendorKey) {
          return;
        }

        if (supportsQuantity && currentCategoryTile.index !== chosenVendorKey) {
          setChosenVendorKey(currentCategoryTile.index);
          return;
        }

        if (!supportsQuantity && (!isDefaultCategory || sponsoredVendorId)) {
          const firstSupportedTile = tiles.find(
            (t) =>
              canVendorSupportQuantity(t.vendor, quantity, sponsoredVendorId, t.category, vendorOptions).canSupport,
          );
          if (firstSupportedTile && !isCalculating) {
            userHasSelectedRef.current = false;
            setSelectedCategory(firstSupportedTile.category);
            setChosenVendorKey(firstSupportedTile.index);
            return;
          }
        }
      }
    }

    if (selectedCategory !== "default" || sponsoredVendorId) {
      const firstSupportedTile = tiles.find(
        (t) => canVendorSupportQuantity(t.vendor, quantity, sponsoredVendorId, t.category, vendorOptions).canSupport,
      );
      if (firstSupportedTile && !isCalculating) {
        userHasSelectedRef.current = false;
        setSelectedCategory(firstSupportedTile.category);
        setChosenVendorKey(firstSupportedTile.index);
      } else if (!isCalculating) {
        setSelectedCategory(null);
      }
    }
  }, [
    vendorOptions,
    chosenVendorKey,
    tiles,
    selectedCategory,
    quantity,
    setChosenVendorKey,
    isCalculating,
    isDefaultVendor,
    sponsoredVendorId,
  ]);

  return (
    <div className={style["vendor-tiles-container"]}>
      <div className={style["vendor-tiles-header"]}>
        <h3 className={style["vendor-tiles-title"]}>Vendor Options</h3>
        <a href="#vendor-options" className={style["see-all-options-link"]} onClick={() => onSeeAllOptions?.()}>
          See All Options
        </a>
      </div>

      <div className={style["vendor-tiles-list"]}>
        {tiles.map((tile) => {
          const isDefaultCategory = tile.category === "default";
          const isAuthorizedCategory = tile.category === "authorized";

          const quantityCheck =
            isDefaultCategory && !sponsoredVendorId
              ? { canSupport: true }
              : canVendorSupportQuantity(tile.vendor, quantity, sponsoredVendorId, tile.category, vendorOptions);
          const isSelected = selectedCategory === tile.category;

          const isDisabled = !quantityCheck.canSupport && !isCalculating;

          const disabledReason =
            tile.vendor?.inventory !== undefined && !isCalculating
              ? `Max qty: ${tile.vendor.inventory}`
              : quantityCheck.maxQty !== undefined && !isCalculating
                ? isAuthorizedCategory && tile.vendor?.vendorName
                  ? `Max qty: ${quantityCheck.maxQty} - ${tile.vendor.vendorName}`
                  : `Max qty: ${quantityCheck.maxQty}`
                : undefined;

          return (
            <BuyBoxTile
              key={`${tile.category}-${tile.index}`}
              category={tile.category}
              vendorOption={tile.vendor}
              isSelected={isSelected}
              isDisabled={isDisabled}
              disabledReason={disabledReason}
              quantity={quantity}
              onSelect={() => {
                if (isDefaultCategory || quantityCheck.canSupport) {
                  userHasSelectedRef.current = true;
                  setChosenVendorKey(tile.index);
                  setSelectedCategory(tile.category);
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export default VendorTiles;
